/**
 * The ops plane's own sign-in. It is served from a separate origin, and
 * Firebase persists a session per origin, so a sign-in on the tenant site is
 * never visible here. After a successful sign-in the session changes and
 * `OpsShell` asks the API again; there is nowhere to navigate to.
 */

import { useState, type FormEvent, type ReactNode } from 'react';

import { describeAuthFailure, useAuthSession } from '../../app/auth/AuthProvider';
import { t } from '../../i18n';
import { toAuthFailure } from '../../lib/auth';
import { Button } from '@/ui/button';
import { Input } from '@/ui/input';
import { Label } from '@/ui/label';

export function OpsSignIn(): ReactNode {
  const { session } = useAuthSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function attempt(signIn: () => Promise<void>): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      await signIn();
    } catch (cause) {
      setError(describeAuthFailure(toAuthFailure(cause)));
    } finally {
      setSubmitting(false);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void attempt(() => session.signIn(email, password));
  }

  return (
    <section className="mx-auto flex max-w-sm flex-col gap-4 p-8">
      <h1 className="text-title-md">{t('auth.signIn')}</h1>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid gap-1.5">
          <Label htmlFor="ops-sign-in-email">{t('auth.email')}</Label>
          <Input
            id="ops-sign-in-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="ops-sign-in-password">{t('auth.password')}</Label>
          <Input
            id="ops-sign-in-password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
        {error !== null && (
          <p role="alert" className="text-destructive text-body-sm">
            {error}
          </p>
        )}
        <Button type="submit" disabled={submitting} className="w-full">
          {t('auth.submit')}
        </Button>
      </form>

      <Button
        type="button"
        variant="outline"
        disabled={submitting}
        onClick={() => void attempt(session.signInWithGoogle)}
      >
        {t('auth.google')}
      </Button>
    </section>
  );
}
